// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useContext } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format, differenceInMonths, isWithinInterval, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import './ChartStyles.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * Deliveries Over Time Chart Component
 * 
 * - Uses daily aggregation if time range is below 1 month
 * - Uses weekly aggregation if time range is below 3 months
 * - Uses monthly aggregation if time range is more than 3 months
 * - Displays tonnage if ≥70% of deliveries have weight, otherwise shows delivery count
 * - Shows stacked bars when a material filter is applied: gray for total, blue for filtered material
 */
function DeliveriesTimeChart({ deliveries = [], startDate, endDate, materialFilter = 'all' }) {
  const MIN_DELIVERIES_FRACTION_FOR_TONNAGE = 0.7; // 70% of deliveries must have weight to display tonnage

  const { t } = useTranslation();
  const { labelsets } = useContext(AuthContext);
  
  // Get material display name based on material key
  const getMaterialDisplayName = (materialKey) => {
    if (materialKey === 'all') return t('all_materials');
    return labelsets?.materials?.labels?.[materialKey]?.display_name || materialKey || 'Unknown';
  };
  
  // Get display name for the current material filter
  const materialDisplayName = getMaterialDisplayName(materialFilter);
  
  // Default to current date if not provided
  const effectiveEndDate = endDate || new Date();
  const effectiveStartDate = startDate || new Date(effectiveEndDate.getFullYear(), 0, 1); // Default to Jan 1st of current year
  
  // Process and group deliveries based on date range
  const chartData = useMemo(() => {
    if (!deliveries || deliveries.length === 0) {
      return { labels: [], values: [], materialValues: [], displayTonnage: false };
    }

    // Check if at least 50% of deliveries have weight registered
    const deliveriesWithWeight = deliveries.filter(delivery => {
      const weightKg = delivery.documents_data?.complete_data?.weight_kg_netto_2 || 
                      delivery.documents_data?.complete_data?.weight_kg_netto_1;
      return weightKg && parseFloat(weightKg) > 0;
    });
    
    const displayTonnage = deliveriesWithWeight.length / deliveries.length >= MIN_DELIVERIES_FRACTION_FOR_TONNAGE;

    // Determine the aggregation level based on date range
    const monthDiff = differenceInMonths(effectiveEndDate, effectiveStartDate);
    let aggregationType = 'daily';
    
    if (monthDiff >= 2) {
      aggregationType = 'monthly';
    } else if (monthDiff >= 1) {
      aggregationType = 'weekly';
    }

    // Create date periods for grouping
    const periods = [];
    let currentDate = new Date(effectiveStartDate);
    
    if (aggregationType === 'daily') {
      while (currentDate <= effectiveEndDate) {
        periods.push({
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
          label: format(currentDate, 'dd MMM')
        });
        currentDate = addDays(currentDate, 1);
      }
    } else if (aggregationType === 'weekly') {
      while (currentDate <= effectiveEndDate) {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 }); // Sunday
        periods.push({
          start: weekStart,
          end: weekEnd,
          label: `${format(weekStart, 'dd MMM')} - ${format(weekEnd, 'dd MMM')}`
        });
        currentDate = addWeeks(currentDate, 1);
      }
    } else {
      // Monthly aggregation
      while (currentDate <= effectiveEndDate) {
        periods.push({
          start: startOfMonth(currentDate),
          end: endOfMonth(currentDate),
          label: format(currentDate, 'MMM yyyy')
        });
        currentDate = addMonths(currentDate, 1);
      }
    }

    // Group deliveries into periods
    const periodData = periods.map(period => {
      const periodDeliveries = deliveries.filter(delivery => {
        // Check if delivery date is in the period
        if (!delivery.date) return false;
        
        // Handle Firestore Timestamp objects
        const deliveryDate = delivery.date.toDate ? delivery.date.toDate() : new Date(delivery.date);
        
        return isWithinInterval(deliveryDate, {
          start: period.start,
          end: period.end
        });
      });
      
      // Get deliveries for the selected material
      const materialDeliveries = materialFilter !== 'all' ? periodDeliveries.filter(delivery => 
        delivery.base_labels?.materials === materialFilter
      ) : [];
      
      if (displayTonnage) {
        // Sum the weight of all deliveries in this period
        const totalWeightKg = periodDeliveries.reduce((sum, delivery) => {
          const weightKg = delivery.documents_data?.complete_data?.weight_kg_netto_2 || 
                          delivery.documents_data?.complete_data?.weight_kg_netto_1;
          return sum + (weightKg ? parseFloat(weightKg) : 0);
        }, 0);
        
        // Sum the weight of material-specific deliveries
        const materialWeightKg = materialDeliveries.reduce((sum, delivery) => {
          const weightKg = delivery.documents_data?.complete_data?.weight_kg_netto_2 || 
                          delivery.documents_data?.complete_data?.weight_kg_netto_1;
          return sum + (weightKg ? parseFloat(weightKg) : 0);
        }, 0);
        
        return {
          total: totalWeightKg / 1000, // Convert kg to tons
          material: materialWeightKg / 1000 // Convert kg to tons
        };
      } else {
        // Return the count of deliveries
        return {
          total: periodDeliveries.length,
          material: materialDeliveries.length
        };
      }
    });

    return {
      labels: periods.map(p => p.label),
      totalValues: periodData.map(d => d.total),
      materialValues: periodData.map(d => d.material),
      displayTonnage
    };
  }, [deliveries, effectiveStartDate, effectiveEndDate, materialFilter]);

  // Chart.js options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        display: materialFilter !== 'all',
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            const datasetLabel = context.dataset.label;
            if (chartData.displayTonnage) {
              return `${datasetLabel}: ${context.raw.toFixed(2)} ${t('tons')}`;
            } else {
              return `${datasetLabel}: ${context.raw} ${context.raw === 1 ? t('delivery_singular') : t('deliveries_plural')}`;
            }
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: t('date'),
          color: '#666',
          font: {
            size: 12,
          },
        },
        stacked: materialFilter !== 'all',
      },
      y: {
        title: {
          display: true,
          text: chartData.displayTonnage ? t('weight_tons') : t('number_of_deliveries'),
          color: '#666',
          font: {
            size: 12,
          },
        },
        beginAtZero: true,
        stacked: materialFilter !== 'all',
        ticks: {
          precision: chartData.displayTonnage ? 2 : 0, // Show decimal places for weight
        },
      },
    },
  };

  // Chart.js data config
  const data = {
    labels: chartData.labels,
    datasets: materialFilter === 'all' ? [
      {
        label: chartData.displayTonnage ? t('weight') : t('deliveries'),
        data: chartData.totalValues,
        backgroundColor: 'rgba(37, 99, 235, 0.6)',
        borderColor: 'rgba(37, 99, 235, 1)',
        borderWidth: 1,
        borderRadius: 4,
        barThickness: 'flex',
        maxBarThickness: 35,
      }
    ] : [
      // For stacked chart, display the filtered material first, then the remaining portion
      {
        label: materialDisplayName,
        data: chartData.materialValues,
        backgroundColor: 'rgba(37, 99, 235, 0.7)', // Blue color for filtered material
        borderColor: 'rgba(37, 99, 235, 1)',
        borderWidth: 1,
        borderRadius: {
          topLeft: 4,
          topRight: 4,
          bottomLeft: 0,
          bottomRight: 0
        },
        barThickness: 'flex',
        maxBarThickness: 35,
      },
      {
        label: t('other_materials'),
        // Calculate the difference between total and material-specific values
        data: chartData.totalValues.map((total, index) => {
          const material = chartData.materialValues[index];
          return Math.max(0, total - material); // Ensure no negative values
        }),
        backgroundColor: 'rgba(160, 174, 192, 0.4)', // Gray color for other materials
        borderColor: 'rgba(160, 174, 192, 0.8)',
        borderWidth: 1,
        borderRadius: {
          topLeft: 0,
          topRight: 0,
          bottomLeft: 4,
          bottomRight: 4
        },
        barThickness: 'flex',
        maxBarThickness: 35,
      }
    ],
  };

  return (
    <div className="deliveries-time-chart">
      {deliveries && deliveries.length > 0 ? (
        <div className="chart-container">
          <Bar options={options} data={data} />
        </div>
      ) : (
        <div className="no-data-message">
          {t('no_delivery_data')}
        </div>
      )}
    </div>
  );
}

export default DeliveriesTimeChart; 